/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const TRANSLATIONS = {
  en: {
    nav: { features: "Features", platforms: "Platforms", pricing: "Pricing", launch: "Launch App" },
    hero: {
      badge: "Free 30-day trial · instantly live on all platforms",
      title1: "Your chat,",
      title2: "read out loud.",
      title3: "Live.",
      desc: "LUCRÜ is the AI voice co-host that reads your live chat to you in real time, reacts with witty comments, and keeps you focused on streaming — not the chat window.",
      cta: "Start reading my chat",
      learn: "See how it works",
      stat_voices: "ultra-realistic voices",
      stat_platforms: "live platforms",
      stat_pwa: "PWA ready",
    },
    features: {
      kicker: "What you get",
      title_a: "Built for streamers who ",
      title_b: "actually play",
      tts_title: "ElevenLabs voices",
      tts_desc: "Pick from dozens of ultra-realistic voices — French, English, Spanish and more. Your co-host sounds human, not robotic.",
      ai_title: "Smart reactions",
      ai_desc: "Toggle smart mode and let Claude Sonnet add a witty one-liner between reads.",
      mod_title: "Anti-spam & mod",
      mod_desc: "Ban words, ignore users, set min/max length. Only the good stuff reaches your ears.",
      stats_title: "Live stats dashboard",
      stats_desc: "See top chatters, messages read, and session uptime — all in a neon dashboard built for 1440p.",
    },
    platforms: { kicker: "Platforms", title: "Where streamers live.", live_auto: "Live now · auto chat", live_relay: "Live · via mobile relay", soon: "Coming soon" },
    pricing: {
      kicker: "Pricing",
      title: "Pick a plan. Keep streaming.",
      monthly: "Monthly",
      yearly: "Yearly · save 20%",
      per_month: "/month",
      per_year: "/year",
      recommended: "Most popular",
      cta: "Start",
      current: "Current plan",
      manage: "Manage billing",
      trial: "Try free for 30 days",
    },
    dashboard: {
      not_connected: "Not connected",
      live: "Live",
      muted: "Muted",
      pause: "Pause",
      start: "Start",
      settings: "Settings",
      connect: "Connect",
      agent: "Agent",
      chat: "Chat",
    },
    footer: { licensed: "LUCRÜ™ is a licensed product of LUCRÜ", rights: "© 2026 LUCRÜ · All rights reserved", powered: "Powered by ElevenLabs + Claude Sonnet" },
  },
  fr: {
    nav: { features: "Fonctionnalités", platforms: "Plateformes", pricing: "Tarifs", launch: "Ouvrir l'app" },
    hero: {
      badge: "Essai gratuit 30 jours · live sur toutes les plateformes",
      title1: "Ton chat,",
      title2: "lu à voix haute.",
      title3: "En live.",
      desc: "LUCRÜ est le co-animateur IA qui lit ton chat en temps réel, réagit avec des piques intelligentes, et te laisse jouer sans quitter l'écran.",
      cta: "Commencer à lire mon chat",
      learn: "Voir comment ça marche",
      stat_voices: "voix ultra-réalistes",
      stat_platforms: "plateformes live",
      stat_pwa: "PWA prête",
    },
    features: {
      kicker: "Ce que tu obtiens",
      title_a: "Pensé pour les streamers qui ",
      title_b: "jouent vraiment",
      tts_title: "Voix ElevenLabs",
      tts_desc: "Des dizaines de voix ultra-réalistes — français, anglais, espagnol et plus. Ton co-animateur sonne humain, pas robotique.",
      ai_title: "Réactions intelligentes",
      ai_desc: "Active le mode smart et laisse Claude Sonnet ajouter une punchline entre deux lectures.",
      mod_title: "Anti-spam & modération",
      mod_desc: "Bannis des mots, ignore des users, règle la longueur min/max. Seul le meilleur arrive à tes oreilles.",
      stats_title: "Stats live",
      stats_desc: "Top chatters, messages lus, uptime de session — tout dans un dashboard néon pensé pour le 1440p.",
    },
    platforms: { kicker: "Plateformes", title: "Là où les streamers vivent.", live_auto: "En direct · chat auto", live_relay: "En direct · via mobile", soon: "Bientôt" },
    pricing: {
      kicker: "Tarifs",
      title: "Choisis un plan. Continue de streamer.",
      monthly: "Mensuel",
      yearly: "Annuel · -20%",
      per_month: "/mois",
      per_year: "/an",
      recommended: "Le plus choisi",
      cta: "Commencer",
      current: "Plan actuel",
      manage: "Gérer l'abonnement",
      trial: "Essai gratuit 30 jours",
    },
    dashboard: {
      not_connected: "Non connecté",
      live: "Live",
      muted: "Muet",
      pause: "Pause",
      start: "Lancer",
      settings: "Réglages",
      connect: "Connecter",
      agent: "Agent",
      chat: "Chat",
    },
    footer: { licensed: "LUCRÜ™ est un produit sous licence de LUCRÜ", rights: "© 2026 LUCRÜ · Tous droits réservés", powered: "Propulsé par ElevenLabs + Claude Sonnet" },
  },
  es: {
    nav: { features: "Funciones", platforms: "Plataformas", pricing: "Precios", launch: "Abrir app" },
    hero: {
      badge: "Prueba gratis 30 días · en vivo en todas las plataformas",
      title1: "Tu chat,",
      title2: "leído en voz alta.",
      title3: "En directo.",
      desc: "LUCRÜ es el co-anfitrión de IA que lee tu chat en tiempo real, reacciona con comentarios ingeniosos y te mantiene centrado en streamear.",
      cta: "Empezar a leer mi chat",
      learn: "Ver cómo funciona",
      stat_voices: "voces ultrarrealistas",
      stat_platforms: "plataformas en vivo",
      stat_pwa: "PWA lista",
    },
    features: {
      kicker: "Qué obtienes",
      title_a: "Hecho para streamers que ",
      title_b: "sí juegan",
      tts_title: "Voces ElevenLabs",
      tts_desc: "Decenas de voces ultrarrealistas — español, inglés, francés y más.",
      ai_title: "Reacciones inteligentes",
      ai_desc: "Activa el modo smart y deja que Claude añada una frase ingeniosa entre lecturas.",
      mod_title: "Anti-spam y mod",
      mod_desc: "Bloquea palabras, ignora usuarios, ajusta longitud. Solo lo bueno llega a tus oídos.",
      stats_title: "Stats en vivo",
      stats_desc: "Top chatters, mensajes leídos, tiempo de sesión — todo en un dashboard neón.",
    },
    platforms: { kicker: "Plataformas", title: "Donde los streamers viven.", live_auto: "En directo · chat auto", live_relay: "En directo · vía móvil", soon: "Próximamente" },
    pricing: {
      kicker: "Precios",
      title: "Elige un plan. Sigue streameando.",
      monthly: "Mensual",
      yearly: "Anual · -20%",
      per_month: "/mes",
      per_year: "/año",
      recommended: "Más popular",
      cta: "Empezar",
      current: "Plan actual",
      manage: "Gestionar suscripción",
      trial: "Prueba gratis 30 días",
    },
    dashboard: {
      not_connected: "Sin conexión",
      live: "En vivo",
      muted: "Silenciado",
      pause: "Pausa",
      start: "Iniciar",
      settings: "Ajustes",
      connect: "Conectar",
      agent: "Agente",
      chat: "Chat",
    },
    footer: { licensed: "LUCRÜ™ es un producto licenciado por LUCRÜ", rights: "© 2026 LUCRÜ · Todos los derechos reservados", powered: "Impulsado por ElevenLabs + Claude Sonnet" },
  },
  pt: {
    nav: { features: "Recursos", platforms: "Plataformas", pricing: "Preços", launch: "Abrir app" },
    hero: {
      badge: "Teste grátis 30 dias · ao vivo em todas as plataformas",
      title1: "Seu chat,",
      title2: "lido em voz alta.",
      title3: "Ao vivo.",
      desc: "LUCRÜ é o co-anfitrião de IA que lê o chat em tempo real, reage com comentários afiados e te deixa focar na stream.",
      cta: "Começar a ler meu chat",
      learn: "Veja como funciona",
      stat_voices: "vozes ultrarrealistas",
      stat_platforms: "plataformas ao vivo",
      stat_pwa: "PWA pronto",
    },
    features: {
      kicker: "O que você ganha",
      title_a: "Feito pra streamers que ",
      title_b: "jogam de verdade",
      tts_title: "Vozes ElevenLabs",
      tts_desc: "Dezenas de vozes ultrarrealistas — português, inglês, espanhol e mais.",
      ai_title: "Reações inteligentes",
      ai_desc: "Ative o smart mode e deixe Claude soltar uma tirada entre leituras.",
      mod_title: "Anti-spam e mod",
      mod_desc: "Banne palavras, ignore users, ajuste tamanho. Só o bom chega aos ouvidos.",
      stats_title: "Stats ao vivo",
      stats_desc: "Top chatters, mensagens lidas, uptime — tudo em um dashboard neon.",
    },
    platforms: { kicker: "Plataformas", title: "Onde os streamers vivem.", live_auto: "Ao vivo · chat auto", live_relay: "Ao vivo · via mobile", soon: "Em breve" },
    pricing: {
      kicker: "Preços",
      title: "Escolha um plano. Siga streamando.",
      monthly: "Mensal",
      yearly: "Anual · -20%",
      per_month: "/mês",
      per_year: "/ano",
      recommended: "Mais popular",
      cta: "Começar",
      current: "Plano atual",
      manage: "Gerenciar assinatura",
      trial: "Teste grátis 30 dias",
    },
    dashboard: {
      not_connected: "Desconectado",
      live: "Ao vivo",
      muted: "Mudo",
      pause: "Pausar",
      start: "Iniciar",
      settings: "Configs",
      connect: "Conectar",
      agent: "Agente",
      chat: "Chat",
    },
    footer: { licensed: "LUCRÜ™ é um produto licenciado pela LUCRÜ", rights: "© 2026 LUCRÜ · Todos os direitos reservados", powered: "Impulsionado por ElevenLabs + Claude Sonnet" },
  },
  de: {
    nav: { features: "Funktionen", platforms: "Plattformen", pricing: "Preise", launch: "App öffnen" },
    hero: {
      badge: "30 Tage gratis · sofort live auf allen Plattformen",
      title1: "Dein Chat,",
      title2: "laut vorgelesen.",
      title3: "Live.",
      desc: "LUCRÜ ist der KI-Co-Host, der deinen Live-Chat in Echtzeit vorliest und mit frechen Kommentaren reagiert — damit du streamen kannst.",
      cta: "Chat vorlesen lassen",
      learn: "So funktioniert's",
      stat_voices: "ultra-realistische Stimmen",
      stat_platforms: "Live-Plattformen",
      stat_pwa: "PWA bereit",
    },
    features: {
      kicker: "Was du bekommst",
      title_a: "Gebaut für Streamer die ",
      title_b: "wirklich spielen",
      tts_title: "ElevenLabs Stimmen",
      tts_desc: "Dutzende ultra-realistische Stimmen — Deutsch, Englisch, Französisch und mehr.",
      ai_title: "Smarte Reaktionen",
      ai_desc: "Aktiviere Smart-Mode und lass Claude eine Pointe einwerfen.",
      mod_title: "Anti-Spam & Mod",
      mod_desc: "Wörter sperren, Nutzer ignorieren, Länge einstellen. Nur das Beste erreicht dich.",
      stats_title: "Live-Stats",
      stats_desc: "Top-Chatter, gelesene Nachrichten, Session-Zeit — alles im Neon-Dashboard.",
    },
    platforms: { kicker: "Plattformen", title: "Wo Streamer leben.", live_auto: "Live · auto Chat", live_relay: "Live · über Mobile", soon: "Bald verfügbar" },
    pricing: {
      kicker: "Preise",
      title: "Wähl einen Plan. Stream weiter.",
      monthly: "Monatlich",
      yearly: "Jährlich · -20%",
      per_month: "/Monat",
      per_year: "/Jahr",
      recommended: "Beliebteste",
      cta: "Starten",
      current: "Aktueller Plan",
      manage: "Abo verwalten",
      trial: "30 Tage gratis testen",
    },
    dashboard: {
      not_connected: "Nicht verbunden",
      live: "Live",
      muted: "Stumm",
      pause: "Pause",
      start: "Start",
      settings: "Einst.",
      connect: "Verbinden",
      agent: "Agent",
      chat: "Chat",
    },
    footer: { licensed: "LUCRÜ™ ist ein lizenziertes Produkt von LUCRÜ", rights: "© 2026 LUCRÜ · Alle Rechte vorbehalten", powered: "Powered by ElevenLabs + Claude Sonnet" },
  },
};

export const SUPPORTED_LOCALES = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const I18nContext = createContext({ locale: "en", t: (k) => k, setLocale: () => {} });

const detectInitial = () => {
  if (typeof window === "undefined") return "en";
  const stored = localStorage.getItem("golucru_locale");
  if (stored && TRANSLATIONS[stored]) return stored;
  const nav = (navigator.language || "en").slice(0, 2);
  return TRANSLATIONS[nav] ? nav : "en";
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectInitial);

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem("golucru_locale", locale);
  }, [locale]);

  const value = useMemo(() => {
    const dict = TRANSLATIONS[locale] || TRANSLATIONS.en;
    const t = (path, fallback = "") => {
      const segs = path.split(".");
      let v = dict;
      for (const s of segs) {
        if (v == null) break;
        v = v[s];
      }
      if (v == null) {
        // fallback to EN then raw path
        let fb = TRANSLATIONS.en;
        for (const s of segs) {
          if (fb == null) break;
          fb = fb[s];
        }
        return fb || fallback || path;
      }
      return v;
    };
    return { locale, setLocale: setLocaleState, t };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
